import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Eye, Play, RefreshCw, PiggyBank } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

// Same chain/job-poll shape as ReceiptCohortNormalizationRecomputePanel.jsx
// (services/bank_fee_cashback.py's Preview/Apply chunked_chain). The cohort
// (transactions with category in bank_fee/cashback, unreconciled) is not
// provably bounded, so it's chunked_chain per CLAUDE.md section 13 even
// though it's usually small.
const POLL_INTERVAL_MS = 2000;
const HEARTBEAT_STALE_SECONDS = 60;

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

function secondsSince(isoString) {
  if (!isoString) return null;
  const then = new Date(isoString).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / 1000));
}

function useJobPolling({ statusFn, onCompleted, onFailed, onProgress }) {
  const [polling, setPolling] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);
  const elapsedRef = useRef(null);
  const startTimeRef = useRef(null);

  const stop = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (elapsedRef.current) { clearInterval(elapsedRef.current); elapsedRef.current = null; }
    startTimeRef.current = null;
    setPolling(false);
  }, []);

  useEffect(() => stop, [stop]);

  const start = useCallback((jobId) => {
    stop();
    setPolling(true);
    setElapsed(0);
    startTimeRef.current = Date.now();

    elapsedRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);

    pollRef.current = setInterval(async () => {
      try {
        const { data } = await statusFn(jobId);
        if (data.status === 'completed') {
          stop();
          onCompleted(data.result);
        } else if (data.status === 'failed') {
          stop();
          onFailed(data.error, data.progress || null);
        } else if (data.status === 'running') {
          onProgress(data.progress || null);
        }
      } catch (err) {
        stop();
        toast.error(getErrorMessage(err));
      }
    }, POLL_INTERVAL_MS);
  }, [stop, statusFn, onCompleted, onFailed, onProgress]);

  return { polling, elapsed, start };
}

const SAMPLE_DISPLAY_LIMIT = 10;

function SampleRows({ operation, samples, testIdPrefix = 'bank-fee-cashback' }) {
  if (!samples || samples.length === 0) return null;
  const shown = samples.slice(0, SAMPLE_DISPLAY_LIMIT);
  return (
    <div className="text-xs space-y-1 pt-1" data-testid={`${testIdPrefix}-${operation}-samples`}>
      <p className="text-muted-foreground">
        Sample transactions (first {shown.length} of {samples.length}):
      </p>
      <div className="border rounded overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="text-left font-medium px-2 py-1">Transaction</th>
              <th className="text-left font-medium px-2 py-1">Source</th>
              <th className="text-left font-medium px-2 py-1">Category</th>
              <th className="text-left font-medium px-2 py-1">Amount</th>
              <th className="text-left font-medium px-2 py-1">Counterparty</th>
              <th className="text-left font-medium px-2 py-1">Outcome</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((s) => (
              <tr key={s.id} className="border-b last:border-b-0">
                <td className="px-2 py-1 font-mono">{s.id}</td>
                <td className="px-2 py-1">{s.source ?? '-'}</td>
                <td className="px-2 py-1">{s.category ?? '-'}</td>
                <td className="px-2 py-1 tabular-nums">{s.amount} {s.currency}</td>
                <td className="px-2 py-1 text-muted-foreground">{s.counterparty ?? '-'}</td>
                <td className="px-2 py-1">{s.outcome}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const _AMBER_TALLY_KEYS = new Set([
  'skipped_conflict', 'owned_by_other_matcher', 'direction_mismatch',
  'direction_mismatch_excluded', 'unresolvable_capped', 'unresolvable_exhausted',
  'unexpected_type',
]);

function ChainSection({
  operation, title, icon, chain, progress, failed, polling, elapsed, starting, stopping,
  onRun, onStop, runLabel, runDisabled, showSamples, testIdPrefix = 'bank-fee-cashback',
  phaseLabel,
}) {
  const running = chain?.status === 'running';
  const live = progress || chain?.seed;
  const cohortCount = chain?.metadata?.cohort_count;
  const processed = live?.cohort_processed;
  const heartbeatAge = secondsSince(chain?.heartbeat_at);
  const stalled = running && !polling && heartbeatAge != null && heartbeatAge > HEARTBEAT_STALE_SECONDS;
  const errorText = (chain?.status === 'error' && chain?.error) || failed;
  const tallies = live?.tallies || {};

  return (
    <div className="border rounded-lg p-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium flex items-center gap-1">{icon}{title}</span>
        <Button data-testid={`${testIdPrefix}-${operation}-run`} onClick={onRun} disabled={runDisabled} size="sm">
          {(starting || polling) ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
          {running
            ? `Chain running, run ${fmtInt(chain.runs_count)}, ${fmtInt(processed)}/${fmtInt(cohortCount)}`
            : runLabel}
        </Button>
        {running && (
          <Button data-testid={`${testIdPrefix}-${operation}-stop`} onClick={onStop} disabled={stopping} size="sm" variant="outline">
            {stopping ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Stop Chain
          </Button>
        )}
        {polling && (
          <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
            <RefreshCw className="h-3 w-3 animate-spin" />
            {elapsed}s
          </span>
        )}
      </div>

      {chain && (
        <div className="text-xs space-y-1" data-testid={`${testIdPrefix}-${operation}-chain-summary`}>
          <div className="flex items-center gap-1">
            <Badge variant={running ? 'default' : chain.status === 'error' ? 'destructive' : 'outline'}>
              {chain.status}
            </Badge>
            <span className="text-muted-foreground">
              run {fmtInt(chain.runs_count)}
              {live?.phase && <> -- phase: {live.phase}</>}
              {cohortCount != null && <> -- {fmtInt(processed)} of {fmtInt(cohortCount)} processed</>}
            </span>
            {stalled && (
              <span className="text-amber-600 dark:text-amber-400">
                last active {heartbeatAge}s ago -- watchdog will resume it
              </span>
            )}
          </div>

          {phaseLabel && live?.phase === 'fetching' && live?.profiles && (
            <p className="text-muted-foreground">
              {phaseLabel}: {Object.values(live.profiles).filter((p) => p.resolved).length} of{' '}
              {Object.values(live.profiles).length} Wise profile(s) fully walked.
            </p>
          )}

          {live && (
            <div className="flex flex-wrap gap-3">
              {Object.entries(tallies).filter(([, count]) => count > 0).map(([key, count]) => (
                <span key={key} className={_AMBER_TALLY_KEYS.has(key) ? 'text-amber-600' : ''}>
                  {key}: <strong>{fmtInt(count)}</strong>
                </span>
              ))}
            </div>
          )}

          {errorText && <p className="text-red-600 dark:text-red-400">{errorText}</p>}

          {showSamples && Object.entries(live?.samples || {}).map(([key, rows]) => (
            rows.length > 0 && <SampleRows key={key} operation={`${operation}-${key}`} samples={rows} testIdPrefix={testIdPrefix} />
          ))}
        </div>
      )}
    </div>
  );
}

function CensusTable({ census }) {
  if (!census) return <p className="text-xs text-muted-foreground">Loading census...</p>;
  const rows = census.by_provider_category || [];
  return (
    <div className="space-y-2" data-testid="bank-fee-cashback-census">
      <div className="border rounded overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="text-left font-medium px-2 py-1">Provider</th>
              <th className="text-left font-medium px-2 py-1">Category</th>
              <th className="text-right font-medium px-2 py-1">Total</th>
              <th className="text-right font-medium px-2 py-1">Reconciled</th>
              <th className="text-right font-medium px-2 py-1">Unreconciled (eligible)</th>
              <th className="text-right font-medium px-2 py-1">Owned by other matcher</th>
              <th className="text-right font-medium px-2 py-1">Direction mismatch</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td className="px-2 py-2 text-muted-foreground" colSpan={7}>No bank_fee/cashback rows found.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={`${r.source}:${r.category}`} className="border-b last:border-b-0">
                <td className="px-2 py-1">{r.source}</td>
                <td className="px-2 py-1">{r.category}</td>
                <td className="px-2 py-1 text-right tabular-nums">{fmtInt(r.total)}</td>
                <td className="px-2 py-1 text-right tabular-nums">{fmtInt(r.reconciled)}</td>
                <td className="px-2 py-1 text-right tabular-nums font-medium">{fmtInt(r.unreconciled_eligible)}</td>
                <td className="px-2 py-1 text-right tabular-nums text-amber-600">{fmtInt(r.owned_by_other_matcher)}</td>
                <td className="px-2 py-1 text-right tabular-nums text-amber-600">{fmtInt(r.direction_mismatch)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        Wise CARD_CASHBACK activity type: confirmed real, but never persisted on the
        transaction document (only used to derive payment_method) -- not classifiable
        from stored data until ingestion captures it. TrueLayer transaction_category:
        fetched by the adapter but dropped before the Mongo write -- only "PURCHASE" has
        ever been observed in this codebase, no fee/cashback value confirmed. Neither is
        guessed into the allowlist.
      </p>
      {census.enablebanking_raw_field_keys_sample && Object.keys(census.enablebanking_raw_field_keys_sample).length > 0 && (
        <p className="text-xs text-muted-foreground">
          EnableBanking eb_raw_fields keys observed (sample): {Object.keys(census.enablebanking_raw_field_keys_sample).join(', ')}
        </p>
      )}
      {census.pennylane_bankfeed_embedded_fee_count_informational > 0 && (
        <p className="text-xs text-muted-foreground">
          Pennylane bank-feed rows with a non-zero embedded fee/currency_fee sub-field
          (informational only, not classified by this detector -- a different shape,
          a fee amount inside another transaction, not a whole fee transaction):{' '}
          {fmtInt(census.pennylane_bankfeed_embedded_fee_count_informational)}
        </p>
      )}
    </div>
  );
}

function WiseCensusTable({ census }) {
  if (!census) return <p className="text-xs text-muted-foreground">Loading census...</p>;
  const byType = census.resolved_by_activity_type || {};
  const apiWalkNeeded = census.api_walk_needed;
  return (
    <div className="space-y-2" data-testid="wise-cashback-census">
      <div className="flex flex-wrap gap-4 text-xs">
        <span>Marker (&quot;{census.marker}&quot;) matched, total: <strong>{fmtInt(census.marker_matched_total_count)}</strong></span>
        <span>Marker-matched, stamped cashback: <strong>{fmtInt(census.marker_matched_stamped_count)}</strong></span>
        <span>Marker-matched, reconciled: <strong>{fmtInt(census.marker_matched_reconciled_count)}</strong></span>
        {census.marker_matched_outflow_anomaly_count > 0 && (
          <span className="text-amber-600">
            Marker-matched but NOT an inflow (unexpected -- see samples below): <strong>{fmtInt(census.marker_matched_outflow_anomaly_count)}</strong>
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-4 text-xs">
        <span>Ambiguous rows: <strong>{fmtInt(census.ambiguous_unresolved_count)}</strong></span>
        <span>-- inflow: <strong>{fmtInt(census.ambiguous_inflow_count)}</strong></span>
        <span>-- outflow (never cashback-shaped): <strong>{fmtInt(census.ambiguous_outflow_count)}</strong></span>
        <span className={apiWalkNeeded ? 'text-amber-600' : ''}>
          Marker-less inflow residue (needs the API walk): <strong>{fmtInt(census.marker_less_inflow_residue_count)}</strong>
        </span>
      </div>
      <div className="flex flex-wrap gap-4 text-xs">
        <span>Resolved CARD_CASHBACK: <strong>{fmtInt(byType.CARD_CASHBACK)}</strong></span>
        <span>Resolved CARD_PAYMENT: <strong>{fmtInt(byType.CARD_PAYMENT)}</strong></span>
        <span>Resolved CARD_REFUND: <strong>{fmtInt(byType.CARD_REFUND)}</strong></span>
        {census.cashback_direction_anomaly_count > 0 && (
          <span className="text-amber-600">
            Direction anomalies (clawback/reversal, not stamped cashback): <strong>{fmtInt(census.cashback_direction_anomaly_count)}</strong>
          </span>
        )}
      </div>
      {!apiWalkNeeded ? (
        <p className="text-xs text-muted-foreground">
          API walk not needed: 0 marker-less inflow residue rows. Every ambiguous row is
          either an outflow (never cashback-shaped) or already resolved by the stored
          description marker -- the Preview/Apply chain below completes from stored data
          alone with no Wise API call and no WISE_API_KEY dependency.
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          {fmtInt(census.marker_less_inflow_residue_count)} inflow row(s) carry no marker in
          their description -- these still need the bounded Wise API walk below to resolve.
          Every other ambiguous row (marker-matched or outflow) resolves from stored data
          alone, no API call.
        </p>
      )}
      {census.candidate_scan_sample_description_raw && census.candidate_scan_sample_description_raw.length > 0 && (
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer">
            Raw description sample ({census.candidate_scan_sample_description_raw.length}) --
            every Wise row whose description contains &quot;cashback&quot; (case-insensitive),
            for eyeballing the verbatim stored shape against the measured marker
          </summary>
          <ul className="pl-4 list-disc">
            {census.candidate_scan_sample_description_raw.map((d, i) => (
              <li key={i} className="font-mono">{JSON.stringify(d)}</li>
            ))}
          </ul>
          {census.candidate_scan_truncated && <p>(sample truncated)</p>}
        </details>
      )}
    </div>
  );
}

export function BankFeeCashbackPanel() {
  const [census, setCensus] = useState(null);
  const [censusLoading, setCensusLoading] = useState(false);

  const [previewChain, setPreviewChain] = useState(null);
  const [previewProgress, setPreviewProgress] = useState(null);
  const [previewFailed, setPreviewFailed] = useState(null);
  const [previewStarting, setPreviewStarting] = useState(false);
  const [previewStopping, setPreviewStopping] = useState(false);

  const [applyChain, setApplyChain] = useState(null);
  const [applyProgress, setApplyProgress] = useState(null);
  const [applyFailed, setApplyFailed] = useState(null);
  const [applyStarting, setApplyStarting] = useState(false);
  const [applyStopping, setApplyStopping] = useState(false);

  const [wiseCensus, setWiseCensus] = useState(null);
  const [wiseCensusLoading, setWiseCensusLoading] = useState(false);

  const [wisePreviewChain, setWisePreviewChain] = useState(null);
  const [wisePreviewProgress, setWisePreviewProgress] = useState(null);
  const [wisePreviewFailed, setWisePreviewFailed] = useState(null);
  const [wisePreviewStarting, setWisePreviewStarting] = useState(false);
  const [wisePreviewStopping, setWisePreviewStopping] = useState(false);

  const [wiseApplyChain, setWiseApplyChain] = useState(null);
  const [wiseApplyProgress, setWiseApplyProgress] = useState(null);
  const [wiseApplyFailed, setWiseApplyFailed] = useState(null);
  const [wiseApplyStarting, setWiseApplyStarting] = useState(false);
  const [wiseApplyStopping, setWiseApplyStopping] = useState(false);

  const fetchCensus = useCallback(async () => {
    setCensusLoading(true);
    try {
      const { data } = await diagnosticsAPI.bankFeeCashbackCensus();
      setCensus(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setCensusLoading(false);
    }
  }, []);

  const fetchPreviewChain = useCallback(async () => {
    try {
      const { data } = await diagnosticsAPI.bankFeeCashbackPreviewChainStatus();
      setPreviewChain(data?.chain || null);
    } catch {
      // Non-fatal: the chain summary just won't render/update this tick.
    }
  }, []);

  const fetchApplyChain = useCallback(async () => {
    try {
      const { data } = await diagnosticsAPI.bankFeeCashbackApplyChainStatus();
      setApplyChain(data?.chain || null);
    } catch {
      // Non-fatal.
    }
  }, []);

  const fetchWiseCensus = useCallback(async () => {
    setWiseCensusLoading(true);
    try {
      const { data } = await diagnosticsAPI.wiseCashbackCensus();
      setWiseCensus(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setWiseCensusLoading(false);
    }
  }, []);

  const fetchWisePreviewChain = useCallback(async () => {
    try {
      const { data } = await diagnosticsAPI.wiseCashbackPreviewChainStatus();
      setWisePreviewChain(data?.chain || null);
    } catch {
      // Non-fatal.
    }
  }, []);

  const fetchWiseApplyChain = useCallback(async () => {
    try {
      const { data } = await diagnosticsAPI.wiseCashbackApplyChainStatus();
      setWiseApplyChain(data?.chain || null);
    } catch {
      // Non-fatal.
    }
  }, []);

  const previewPoll = useJobPolling({
    statusFn: diagnosticsAPI.bankFeeCashbackJobStatus,
    onCompleted: useCallback((result) => {
      setPreviewProgress(null);
      setPreviewFailed(null);
      fetchPreviewChain();
      toast.success(result?.truncated ? 'Preview run complete -- continuing chain' : 'Preview complete');
    }, [fetchPreviewChain]),
    onFailed: useCallback((error, lastProgress) => {
      setPreviewProgress((prev) => lastProgress ?? prev);
      setPreviewFailed(error);
      fetchPreviewChain();
      toast.error(error ?? 'Preview run failed');
    }, [fetchPreviewChain]),
    onProgress: useCallback((progress) => setPreviewProgress(progress), []),
  });

  const applyPoll = useJobPolling({
    statusFn: diagnosticsAPI.bankFeeCashbackJobStatus,
    onCompleted: useCallback((result) => {
      setApplyProgress(null);
      setApplyFailed(null);
      fetchApplyChain();
      fetchCensus();
      toast.success(result?.truncated ? 'Apply run complete -- continuing chain' : 'Apply complete');
    }, [fetchApplyChain, fetchCensus]),
    onFailed: useCallback((error, lastProgress) => {
      setApplyProgress((prev) => lastProgress ?? prev);
      setApplyFailed(error);
      fetchApplyChain();
      toast.error(error ?? 'Apply run failed');
    }, [fetchApplyChain]),
    onProgress: useCallback((progress) => setApplyProgress(progress), []),
  });

  const wisePreviewPoll = useJobPolling({
    statusFn: diagnosticsAPI.wiseCashbackJobStatus,
    onCompleted: useCallback((result) => {
      setWisePreviewProgress(null);
      setWisePreviewFailed(null);
      fetchWisePreviewChain();
      toast.success(result?.truncated ? 'Wise cashback preview run complete -- continuing chain' : 'Wise cashback preview complete');
    }, [fetchWisePreviewChain]),
    onFailed: useCallback((error, lastProgress) => {
      setWisePreviewProgress((prev) => lastProgress ?? prev);
      setWisePreviewFailed(error);
      fetchWisePreviewChain();
      toast.error(error ?? 'Wise cashback preview run failed');
    }, [fetchWisePreviewChain]),
    onProgress: useCallback((progress) => setWisePreviewProgress(progress), []),
  });

  const wiseApplyPoll = useJobPolling({
    statusFn: diagnosticsAPI.wiseCashbackJobStatus,
    onCompleted: useCallback((result) => {
      setWiseApplyProgress(null);
      setWiseApplyFailed(null);
      fetchWiseApplyChain();
      fetchWiseCensus();
      toast.success(result?.truncated ? 'Wise cashback apply run complete -- continuing chain' : 'Wise cashback apply complete');
    }, [fetchWiseApplyChain, fetchWiseCensus]),
    onFailed: useCallback((error, lastProgress) => {
      setWiseApplyProgress((prev) => lastProgress ?? prev);
      setWiseApplyFailed(error);
      fetchWiseApplyChain();
      toast.error(error ?? 'Wise cashback apply run failed');
    }, [fetchWiseApplyChain]),
    onProgress: useCallback((progress) => setWiseApplyProgress(progress), []),
  });

  useEffect(() => { fetchCensus(); }, [fetchCensus]);
  useEffect(() => { fetchWiseCensus(); }, [fetchWiseCensus]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.bankFeeCashbackJobLatest('preview');
        const job = data?.job;
        if (job?.status === 'running') {
          setPreviewProgress(job.progress || null);
          previewPoll.start(job._id);
        } else if (job?.status === 'failed') {
          setPreviewProgress(job.progress || null);
          setPreviewFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.bankFeeCashbackJobLatest('apply');
        const job = data?.job;
        if (job?.status === 'running') {
          setApplyProgress(job.progress || null);
          applyPoll.start(job._id);
        } else if (job?.status === 'failed') {
          setApplyProgress(job.progress || null);
          setApplyFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.wiseCashbackJobLatest('preview');
        const job = data?.job;
        if (job?.status === 'running') {
          setWisePreviewProgress(job.progress || null);
          wisePreviewPoll.start(job._id);
        } else if (job?.status === 'failed') {
          setWisePreviewProgress(job.progress || null);
          setWisePreviewFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.wiseCashbackJobLatest('apply');
        const job = data?.job;
        if (job?.status === 'running') {
          setWiseApplyProgress(job.progress || null);
          wiseApplyPoll.start(job._id);
        } else if (job?.status === 'failed') {
          setWiseApplyProgress(job.progress || null);
          setWiseApplyFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchPreviewChain(); }, [fetchPreviewChain]);
  useEffect(() => { fetchApplyChain(); }, [fetchApplyChain]);
  useEffect(() => { fetchWisePreviewChain(); }, [fetchWisePreviewChain]);
  useEffect(() => { fetchWiseApplyChain(); }, [fetchWiseApplyChain]);

  useEffect(() => {
    if (previewChain?.status !== 'running') return undefined;
    const id = setInterval(fetchPreviewChain, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [previewChain?.status, fetchPreviewChain]);

  useEffect(() => {
    if (applyChain?.status !== 'running') return undefined;
    const id = setInterval(fetchApplyChain, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [applyChain?.status, fetchApplyChain]);

  useEffect(() => {
    if (wisePreviewChain?.status !== 'running') return undefined;
    const id = setInterval(fetchWisePreviewChain, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [wisePreviewChain?.status, fetchWisePreviewChain]);

  useEffect(() => {
    if (wiseApplyChain?.status !== 'running') return undefined;
    const id = setInterval(fetchWiseApplyChain, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [wiseApplyChain?.status, fetchWiseApplyChain]);

  const runPreview = async () => {
    setPreviewStarting(true);
    setPreviewFailed(null);
    try {
      const { data } = await diagnosticsAPI.bankFeeCashbackPreviewChainStart();
      if (data.job_id) {
        previewPoll.start(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('A preview chain is already running -- it will continue on its own.');
      } else if (data.status === 'chain_unavailable') {
        toast.error('Chain became unavailable while starting -- try again.');
      }
      fetchPreviewChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewStarting(false);
    }
  };

  const runApply = async () => {
    const cohortCount = previewChain?.metadata?.cohort_count ?? applyChain?.metadata?.cohort_count;
    if (!window.confirm(
      `Reconcile ${cohortCount ?? 'the'} bank-fee/cashback transaction(s) as document-free? ` +
      'No invoice or receipt will ever be required for these.'
    )) return;
    setApplyStarting(true);
    setApplyFailed(null);
    try {
      const { data } = await diagnosticsAPI.bankFeeCashbackApplyChainStart();
      if (data.job_id) {
        applyPoll.start(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('An apply chain is already running -- it will continue on its own.');
      } else if (data.status === 'chain_unavailable') {
        toast.error('Chain became unavailable while starting -- try again.');
      }
      fetchApplyChain();
    } catch (err) {
      const msg = getErrorMessage(err);
      toast.error(msg || 'Failed to start Apply');
      setApplyFailed(msg);
    } finally {
      setApplyStarting(false);
    }
  };

  const stopPreviewChain = async () => {
    if (!previewChain?._id) return;
    if (!window.confirm('Stop the running Preview chain? The current run finishes its time window normally.')) return;
    setPreviewStopping(true);
    try {
      await diagnosticsAPI.bankFeeCashbackPreviewChainStop(previewChain._id);
      toast.success('Stop requested -- the chain will halt after its current run finishes.');
      fetchPreviewChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewStopping(false);
    }
  };

  const stopApplyChain = async () => {
    if (!applyChain?._id) return;
    if (!window.confirm('Stop the running Apply chain? The current run finishes its time window normally.')) return;
    setApplyStopping(true);
    try {
      await diagnosticsAPI.bankFeeCashbackApplyChainStop(applyChain._id);
      toast.success('Stop requested -- the chain will halt after its current run finishes.');
      fetchApplyChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplyStopping(false);
    }
  };

  const runWisePreview = async () => {
    setWisePreviewStarting(true);
    setWisePreviewFailed(null);
    try {
      const { data } = await diagnosticsAPI.wiseCashbackPreviewChainStart();
      if (data.job_id) {
        wisePreviewPoll.start(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('A Wise cashback preview chain is already running -- it will continue on its own.');
      } else if (data.status === 'chain_unavailable') {
        toast.error('Chain became unavailable while starting -- try again.');
      }
      fetchWisePreviewChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setWisePreviewStarting(false);
    }
  };

  const runWiseApply = async () => {
    if (!window.confirm(
      'Resolve ambiguous historical Wise card rows via a bounded Wise API walk, and stamp ' +
      'category=cashback where eligible? This stamps category ONLY -- afterwards, re-run the ' +
      'Bank Fee / Cashback Apply above to actually reconcile the newly-categorised rows.'
    )) return;
    setWiseApplyStarting(true);
    setWiseApplyFailed(null);
    try {
      const { data } = await diagnosticsAPI.wiseCashbackApplyChainStart();
      if (data.job_id) {
        wiseApplyPoll.start(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('A Wise cashback apply chain is already running -- it will continue on its own.');
      } else if (data.status === 'chain_unavailable') {
        toast.error('Chain became unavailable while starting -- try again.');
      }
      fetchWiseApplyChain();
    } catch (err) {
      const msg = getErrorMessage(err);
      toast.error(msg || 'Failed to start Wise cashback Apply');
      setWiseApplyFailed(msg);
    } finally {
      setWiseApplyStarting(false);
    }
  };

  const stopWisePreviewChain = async () => {
    if (!wisePreviewChain?._id) return;
    if (!window.confirm('Stop the running Wise cashback Preview chain?')) return;
    setWisePreviewStopping(true);
    try {
      await diagnosticsAPI.wiseCashbackPreviewChainStop(wisePreviewChain._id);
      toast.success('Stop requested -- the chain will halt after its current run finishes.');
      fetchWisePreviewChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setWisePreviewStopping(false);
    }
  };

  const stopWiseApplyChain = async () => {
    if (!wiseApplyChain?._id) return;
    if (!window.confirm('Stop the running Wise cashback Apply chain?')) return;
    setWiseApplyStopping(true);
    try {
      await diagnosticsAPI.wiseCashbackApplyChainStop(wiseApplyChain._id);
      toast.success('Stop requested -- the chain will halt after its current run finishes.');
      fetchWiseApplyChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setWiseApplyStopping(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <PiggyBank className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Bank Fee / Cashback Reconciliation</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Bank fees and cashback need no invoice or receipt, ever (operator policy, 2026-08-25).
          Revolut and Wise already categorise these structurally at ingestion
          (category=bank_fee) but never reconcile them -- they sit at is_reconciled=false
          forever. This reconciles them as document-free (settled_via=bank_fee_rule /
          cashback_rule), so they leave every matcher candidate pool and never show up as
          a coverage gap. Fail-closed: only category values a provider has already,
          measurably assigned are ever touched.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Phase-0 marker census</span>
          <Button size="sm" variant="outline" onClick={fetchCensus} disabled={censusLoading}>
            {censusLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
            Refresh
          </Button>
        </div>
        <CensusTable census={census} />

        <ChainSection
          operation="preview"
          title="Preview"
          icon={<Eye className="h-3 w-3" />}
          chain={previewChain}
          progress={previewProgress}
          failed={previewFailed}
          polling={previewPoll.polling}
          elapsed={previewPoll.elapsed}
          starting={previewStarting}
          stopping={previewStopping}
          onRun={runPreview}
          onStop={stopPreviewChain}
          runLabel="Preview"
          runDisabled={previewStarting || previewPoll.polling || previewChain?.status === 'running'}
          showSamples
        />
        <ChainSection
          operation="apply"
          title="Apply"
          icon={<Play className="h-3 w-3" />}
          chain={applyChain}
          progress={applyProgress}
          failed={applyFailed}
          polling={applyPoll.polling}
          elapsed={applyPoll.elapsed}
          starting={applyStarting}
          stopping={applyStopping}
          onRun={runApply}
          onStop={stopApplyChain}
          runLabel={previewChain?.metadata?.cohort_count != null ? `Apply (${fmtInt(previewChain.metadata.cohort_count)} candidates)` : 'Apply'}
          runDisabled={applyStarting || applyPoll.polling || applyChain?.status === 'running'}
          showSamples
        />

        <div className="border-t pt-3 space-y-3">
          <div>
            <span className="text-sm font-medium">Wise Cashback Historical Recovery</span>
            <p className="text-xs text-muted-foreground mt-1">
              A historical Wise card row with description exactly "Card cashback" (measured from a
              real prod row, 25 Aug 2026) is stamped category=cashback directly from stored data --
              no Wise API call. An outflow row is never cashback-shaped and is excluded immediately.
              Only a genuinely marker-less INFLOW row still needs the bounded Wise activities API
              walk below to resolve. <strong>Two-step order:</strong> run this Apply first, then
              re-run the Bank Fee / Cashback Apply above to actually reconcile the newly-categorised
              rows -- one shared writer, no duplication.
            </p>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Wise cashback census</span>
            <Button size="sm" variant="outline" onClick={fetchWiseCensus} disabled={wiseCensusLoading}>
              {wiseCensusLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
              Refresh
            </Button>
          </div>
          <WiseCensusTable census={wiseCensus} />

          <ChainSection
            operation="preview"
            title="Preview"
            icon={<Eye className="h-3 w-3" />}
            chain={wisePreviewChain}
            progress={wisePreviewProgress}
            failed={wisePreviewFailed}
            polling={wisePreviewPoll.polling}
            elapsed={wisePreviewPoll.elapsed}
            starting={wisePreviewStarting}
            stopping={wisePreviewStopping}
            onRun={runWisePreview}
            onStop={stopWisePreviewChain}
            runLabel="Preview"
            runDisabled={wisePreviewStarting || wisePreviewPoll.polling || wisePreviewChain?.status === 'running'}
            showSamples
            testIdPrefix="wise-cashback"
            phaseLabel="Fetching Wise activity history"
          />
          <ChainSection
            operation="apply"
            title="Apply"
            icon={<Play className="h-3 w-3" />}
            chain={wiseApplyChain}
            progress={wiseApplyProgress}
            failed={wiseApplyFailed}
            polling={wiseApplyPoll.polling}
            elapsed={wiseApplyPoll.elapsed}
            starting={wiseApplyStarting}
            stopping={wiseApplyStopping}
            onRun={runWiseApply}
            onStop={stopWiseApplyChain}
            runLabel={wisePreviewChain?.metadata?.cohort_count != null ? `Apply (${fmtInt(wisePreviewChain.metadata.cohort_count)} candidates)` : 'Apply'}
            runDisabled={wiseApplyStarting || wiseApplyPoll.polling || wiseApplyChain?.status === 'running'}
            showSamples
            testIdPrefix="wise-cashback"
            phaseLabel="Fetching Wise activity history"
          />
        </div>
      </CardContent>
    </Card>
  );
}
